import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Clock, DollarSign, CalendarPlus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CAREGIVER } from "@/constants/testIds";

export default function ShiftsPage() {
    const { user } = useAuth();
    const [shifts, setShifts] = useState([]);

    async function load() {
        try {
            const { data } = await api.get("/shifts");
            setShifts(data);
        } catch { /* noop */ }
    }
    useEffect(() => { load(); }, []);

    async function claim(id) {
        try { await api.post(`/shifts/${id}/claim`); toast.success("Claimed"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }
    async function complete(id) {
        try { await api.post(`/shifts/${id}/complete`); toast.success("Marked complete"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }
    async function push(id) {
        try {
            const { data } = await api.post(`/calendar/push-shift/${id}`);
            toast.success(data.mocked ? "Calendar push simulated" : "Pushed to Google");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main className="max-w-6xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-2">Shift marketplace</div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
                        All shifts
                    </h1>
                </div>

                <div className="grid gap-3">
                    {shifts.length === 0 && <p className="text-stone-500 font-dmsans">No shifts.</p>}
                    {shifts.map((s) => (
                        <Card key={s.id} className="rounded-2xl border-stone-200 hover:shadow-md transition-shadow">
                            <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-outfit text-lg font-semibold text-stone-900">{s.title}</span>
                                        <Badge className={
                                            s.status === "open" ? "bg-orange-50 text-orange-700 border-orange-100 hover:bg-orange-50" :
                                            s.status === "claimed" ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50" :
                                            "bg-stone-100 text-stone-700 border-stone-200 hover:bg-stone-100"
                                        }>
                                            {s.status}
                                        </Badge>
                                    </div>
                                    <div className="flex flex-wrap gap-4 text-sm text-stone-500 font-dmsans">
                                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {s.location}</span>
                                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {new Date(s.start).toLocaleString()}</span>
                                        <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> ${((s.rate_cents || 0) / 100).toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {user?.role === "caregiver" && s.status === "open" && (
                                        <Button
                                            data-testid={CAREGIVER.claimShiftBtn}
                                            onClick={() => claim(s.id)}
                                            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white h-9 text-xs"
                                        >
                                            Claim
                                        </Button>
                                    )}
                                    {user?.role === "caregiver" && s.caregiver_id === user.id && s.status === "claimed" && (
                                        <>
                                            <Button
                                                data-testid={CAREGIVER.pushCalendarBtn}
                                                onClick={() => push(s.id)}
                                                variant="outline"
                                                className="rounded-full h-9 text-xs"
                                            >
                                                <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Calendar
                                            </Button>
                                            <Button
                                                onClick={() => complete(s.id)}
                                                variant="outline"
                                                className="rounded-full h-9 text-xs"
                                            >
                                                Mark complete
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </main>
        </div>
    );
}
